import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BinariesManager from './BinariesManager';
import ReleaseEditor from './ReleaseEditor';
import GeneratedManifests from './GeneratedManifests';
import AllBinsModal from './AllBinsModal';
import AllManifestsModal from './AllManifestsModal';
import { API_BASE_URL } from '../config/api';


// Interface for manifest files
interface ManifestFile {
  name: string;
  path: string;
}

// Interface for firmware files
interface FirmwareFile {
  name: string;
  path: string;
}

const FirmwareManagement: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generateSuccess, setGenerateSuccess] = useState<boolean>(false);
  const [showAllBinsModal, setShowAllBinsModal] = useState<boolean>(false);
  const [showAllManifestsModal, setShowAllManifestsModal] = useState<boolean>(false);

  // Firmware files list state
  const [firmwareFiles, setFirmwareFiles] = useState<FirmwareFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState<boolean>(false);

  // Manifest files list state
  const [manifestFiles, setManifestFiles] = useState<ManifestFile[]>([]);
  const [loadingManifestFiles, setLoadingManifestFiles] = useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([fetchFirmwareFiles(), fetchManifestFiles()])
      .finally(() => setIsLoading(false));
  }, []);

  const fetchManifestFiles = async () => {
    setLoadingManifestFiles(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/manifest/list`, {
        withCredentials: true
      });
      if (response.data.success) {
        setManifestFiles(response.data.files);
      } else {
        console.error('Error fetching manifest files:', response.data.message);
      }
    } catch (error: any) {
      console.error('Error fetching manifest files:', error);
    } finally {
      setLoadingManifestFiles(false);
    }
  };

  const fetchFirmwareFiles = async () => {
    setLoadingFiles(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/firmware/list`, {
        withCredentials: true
      });
      if (response.data.success) {
        setFirmwareFiles(response.data.files);
      } else {
        console.error('Error fetching firmware files:', response.data.message);
      }
    } catch (error: any) {
      console.error('Error fetching firmware files:', error);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleGenerateManifests = async () => {
    setIsGenerating(true);
    setGenerateSuccess(false);
    try {
      await axios.post(`${API_BASE_URL}/manifest/generate`, {}, {
        withCredentials: true
      });
      setGenerateSuccess(true);
      setTimeout(() => {
        setGenerateSuccess(false);
      }, 3000);
      fetchManifestFiles();
    } catch (error: any) {
      console.error('Error generating manifests:', error);
      alert(error.response?.data?.message || 'Failed to generate manifest files. Please try again.');
      throw error;
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete ${fileName}?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/firmware/${encodeURIComponent(fileName)}`, {
        withCredentials: true
      });
      fetchFirmwareFiles();
    } catch (error: any) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file. Please try again.');
    }
  };

  const handleDeleteManifest = async (fileName: string) => {
    if (!window.confirm(`Are you sure you want to delete manifest ${fileName}?`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/manifest/${encodeURIComponent(fileName)}`, {
        withCredentials: true
      });
      fetchManifestFiles();
    } catch (error: any) {
      console.error('Error deleting manifest file:', error);
      alert('Failed to delete manifest file. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-3">
      <div className="row mb-3">
        <div className="col">
          <h3>Manage Firmware</h3>
        </div>
      </div>

      {/* Release Editor Section */}
      <ReleaseEditor
        firmwareFiles={firmwareFiles}
        limitDisplay={20}
        onGenerateManifests={handleGenerateManifests}
        isGeneratingManifests={isGenerating}
      />

      {/* Combined Upload and Available Binaries Section */}
      <BinariesManager
        firmwareFiles={firmwareFiles}
        loadingFiles={loadingFiles}
        onRefresh={fetchFirmwareFiles}
        onDelete={handleDeleteFile}
        onViewAll={() => setShowAllBinsModal(true)}
        apiBaseUrl={API_BASE_URL}
      />

      {/* Generated Manifest Files Section */}
      <GeneratedManifests
        manifestFiles={manifestFiles}
        loadingManifestFiles={loadingManifestFiles}
        onRefresh={fetchManifestFiles}
        onDelete={handleDeleteManifest}
        apiBaseUrl={API_BASE_URL}
        limitDisplay={6}
        onViewAll={() => setShowAllManifestsModal(true)}
        onGenerate={handleGenerateManifests}
        isGenerating={isGenerating}
      />

      {/* Modal for viewing all bins */}
      <AllBinsModal
        show={showAllBinsModal}
        onClose={() => setShowAllBinsModal(false)}
        firmwareFiles={firmwareFiles}
        onDelete={handleDeleteFile}
        apiBaseUrl={API_BASE_URL}
      />

      {/* Add the All Manifests Modal */}
      <AllManifestsModal
        show={showAllManifestsModal}
        onClose={() => setShowAllManifestsModal(false)}
        manifestFiles={manifestFiles}
        onDelete={handleDeleteManifest}
        apiBaseUrl={API_BASE_URL}
      />
    </div>
  );
};

export default FirmwareManagement;